import { Component, EventEmitter, input, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ButtonName } from '@app/components/booking-flow/booking-flow.component';
import { LSKey } from '@app/enums/localStorageKey.enum';
import { AppointmentRequest } from '@app/models/appointmentRequest.model';
import { Service } from '@app/models/service.model';
import { SiteBookingSettings } from '@app/models/siteBookingSettings.model';
import { FeatureFlagService, FeatureKey } from '@app/services/feature-flag.service';
import { APPOINTMENT_REQUEST, LocalStorageService, SITE_BOOKING_SETTINGS } from '@app/services/local-storage.service';
import { LocaleService } from '@app/services/locale.service';
import { NavigationService } from '@app/services/navigation.service';
import { Employee } from '@models/employee.model';
import { EmployeeService } from '@services/employee.service';
import { from, Subject, Subscription, takeUntil } from 'rxjs';

@Component({
    selector: 'app-select-employee',
    templateUrl: './select-employee.component.html',
    styleUrls: ['./select-employee.component.css'],
    standalone: false,
})
export class SelectEmployeeComponent implements OnInit, OnDestroy, OnChanges {
    @Output() assignSelectedEmployee = new EventEmitter<number>();
    @Output() isValidEmployeeAndService = new EventEmitter<boolean>();
    @Input() public employeeList: Employee[] = [];
    public filteredEmployeeList: Employee[] = [];
    public formGroup: FormGroup;
    public ngUnsubscribe: Subject<any> = new Subject();
    private _destroyed$: Subject<void>;
    private navigationButtonSubscription!: Subscription;
    private isFirstAvailableFFOn = false;
    public isServicePriceAndDurationFeatureActive: boolean = false;
    public minEmployeePrice: number = 0;
    public maxEmployeePrice: number = 0;
    public showDescription: boolean = false;
    private isBookMultiServiceFeatureActive: boolean = false;
    public siteBookingSettings: SiteBookingSettings = new SiteBookingSettings({} as SiteBookingSettings);
    shouldShowServicePrice = false;
    isBookMultiServiceEnabled = false;
    noEmployeeAvailable = false;
    currencyCode: string = '';
    localeId: string = '';
    private isRedesignFeatureActive: boolean = false;
    isLoading = true;
    // override the appt request's services with the passed in services for this component
    // this does not modify the actual stored appointment request until an employee is selected
    @Input() services: Service[] | undefined;
    selectedEmployeeId = 0;
    selectedServicesIds: string[] = [];
    public showEmployeesNotAllowedToPerformService = input<boolean>(false);

    constructor(
        private employeeService: EmployeeService,
        private fb: FormBuilder,
        private localStorageService: LocalStorageService,
        private featureFlagService: FeatureFlagService,
        private navigationService: NavigationService,
        private localeService: LocaleService
    ) {
        this.isFirstAvailableFFOn = this.featureFlagService.getFeatureFlag(FeatureKey.FirstAvailable);
        this.siteBookingSettings = this.localStorageService.get(SITE_BOOKING_SETTINGS) as SiteBookingSettings;
        this.isServicePriceAndDurationFeatureActive = this.featureFlagService.getFeatureFlag(FeatureKey.ShowPriceAndDuration);
        this.shouldShowServicePrice = this.isServicePriceAndDurationFeatureActive && this.siteBookingSettings.showPricesWithEmployees;
        this.isBookMultiServiceEnabled = this.featureFlagService.getFeatureFlag(FeatureKey.BookMultiService);
        this._destroyed$ = new Subject();
        this.formGroup = this.fb.group({
            employee: new FormControl('', Validators.required),
        });
        this.isBookMultiServiceFeatureActive = this.featureFlagService.getFeatureFlag(FeatureKey.BookMultiService);
        this.isRedesignFeatureActive = this.featureFlagService.getFeatureFlag(FeatureKey.Redesign);
    }

    async ngOnInit() {
        this.isLoading = true;
        this.filteredEmployeeList = [...this.employeeList];
        await this.localeService.loadLocaleFile(this.siteBookingSettings.customerCultureCode, true);
        this.localeId = this.localeService.getLocale();
        this.currencyCode = this.localeService.getCurrencyCode() || 'USD';
        const appointmentRequest = (this.localStorageService.get(APPOINTMENT_REQUEST) || {}) as AppointmentRequest;
        if (this.services && this.services.length > 0) {
            appointmentRequest.service = this.services;
        }
        let employeeIdsForSelectedServices: string[] = [];
        if (appointmentRequest.service?.length) {
            employeeIdsForSelectedServices = this.employeeService.getEmployeesFromServiceList(appointmentRequest.service);
            this.selectedServicesIds = appointmentRequest.service.map((service) => service.id);
            this.showDescription = this.isBookMultiServiceFeatureActive && appointmentRequest.service.length > 1;
            if (this.isRedesignFeatureActive && !this.showEmployeesNotAllowedToPerformService()) {
                this.filteredEmployeeList = this.employeeList.filter(
                    (employee) => employee.id === '0' || employeeIdsForSelectedServices.includes(employee.id)
                );
            }
        }

        if (!this.isRedesignFeatureActive) {
            if (employeeIdsForSelectedServices?.length) {
                await this.fetchEmployees(employeeIdsForSelectedServices);
            } else {
                this.noEmployeeAvailable = true;
            }

            this.previouslySelectedEmployee();
        } else {
            this.formGroup.get('employee')?.valueChanges.subscribe((employeeId) => {
                this.selectedEmployeeId = Number(this.filteredEmployeeList.find((e) => e.id === employeeId)?.id) || 0;
                this.assignSelectedEmployee.emit(this.selectedEmployeeId);
                if (this.services) {
                    const canEmployeePerformSelectedServices =
                        this.selectedEmployeeId === 0 ||
                        this.employeeService.getEmployeesFromServiceList(this.services).includes(this.selectedEmployeeId.toString());
                    this.isValidEmployeeAndService.emit(canEmployeePerformSelectedServices);
                }
            });

            if (!appointmentRequest?.employee?.length) {
                this.selectedEmployeeId = 0;
            } else {
                this.selectedEmployeeId = +appointmentRequest.employee[0].id;
            }
            this.formGroup.patchValue({ employee: this.selectedEmployeeId.toString() }, { emitEvent: false });
        }

        /* istanbul ignore next */
        this.navigationButtonSubscription = this.navigationService.getNavigationButtonName().subscribe((buttonName) => {
            if (buttonName === ButtonName.SelectDateTime) {
                const employee = this.filteredEmployeeList.filter((e) => e.id === this.formGroup.value.employee);
                if (employee?.length === 1 && employee[0].id === '0') {
                    employee[0].firstAvailable = true;
                }
                this.localStorageService.setNested(APPOINTMENT_REQUEST, LSKey.Employee, employee);
            }
        });
        this.formGroup.statusChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((status) => {
            if (status === 'VALID') {
                this.navigationService.setIsStepFormValid(true);
            }
        });

        this.isLoading = false;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['employeeList']?.currentValue) {
            this.employeeList = changes['employeeList'].currentValue;
            this.filteredEmployeeList = [...this.employeeList];
            const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
            if (!this.services) {
                this.services = appointmentRequest.service;
            }
            this.selectedServicesIds = this.services?.map((service) => service.id) || [];
            // In the redesign, when the Select Employee component opens, set the First Available Employee as the default.
            this.setFirstAvailable();
            if (this.isRedesignFeatureActive && this.services?.length && !this.showEmployeesNotAllowedToPerformService()) {
                const employeeIdsForSelectedServices = this.employeeService.getEmployeesFromServiceList(this.services);
                this.filteredEmployeeList = this.employeeList.filter(
                    (employee) => employee.id === '0' || employeeIdsForSelectedServices.includes(employee.id)
                );
            }
            if (appointmentRequest?.employee?.length > 0) {
                this.selectedEmployeeId = Number(appointmentRequest.employee[0].id);
            }
            this.formGroup.patchValue({ employee: this.selectedEmployeeId.toString() });
            this.assignSelectedEmployee.emit(this.selectedEmployeeId);
            this.isValidEmployeeAndService.emit(true);
        }
    }

    private fetchEmployees(employeeIdList: string[]): Promise<void> {
        return new Promise<void>((resolve) => {
            from(this.employeeService.getEmployeeList(employeeIdList, this.selectedServicesIds))
                .pipe(takeUntil(this._destroyed$))
                .subscribe((employeeList) => {
                    this.filteredEmployeeList = this.employeeService.sortEmployeeList(employeeList, this.localeId);
                    this.setFirstAvailable();
                    this.formGroup.updateValueAndValidity();
                    resolve();
                });
        });
    }

    private setFirstAvailable() {
        if (this.isFirstAvailableFFOn) {
            const [minPrice, maxPrice] = this.getMinAndMaxEmployeePrice(this.filteredEmployeeList);
            this.minEmployeePrice = minPrice;
            this.maxEmployeePrice = maxPrice;
            const employeeServicePrice = this.employeeService.getEmployeeServicePriceListForFirstAvailability(this.selectedServicesIds);
            if (this.filteredEmployeeList.length >= 2) {
                if (!this.isRedesignFeatureActive) {
                    this.filteredEmployeeList.unshift({
                        id: '0',
                        name: 'First Available',
                        employeeServicePriceList: employeeServicePrice,
                    });
                }
            }
        }
    }

    getEmployeePrice(employee: Employee): number {
        if (this.isFirstAvailableEmployee(employee)) {
            // For First Available, use the lowest price from each service (same as service listing page)
            return this.services?.reduce((sum, service) => sum + (service.lowestPrice ?? service.price ?? 0), 0) ?? 0;
        }

        const employeeServicePrices = this.isRedesignFeatureActive
            ? employee.employeeServicePriceList.filter((esp) => this.selectedServicesIds.includes(esp.serviceID)).map((esp) => esp.price)
            : employee.employeeServicePriceList.map((esp) => esp.price);

        return employeeServicePrices.reduce((sum, price) => sum + price, 0);
    }

    getEmployeeDuration(employee: Employee): number {
        if (!this.services?.length) {
            return 0;
        }

        return this.services.reduce((total, service) => {
            if (this.isFirstAvailableEmployee(employee)) {
                // For First Available, use longestDuration if service has duration exceptions
                return (
                    total + (service.hasDurationException ? (service.longestDuration ?? service.duration ?? 0) : (service.duration ?? 0))
                );
            } else {
                // For specific employee, check if they have a duration exception for this service
                const durationException = employee.durationExceptionList?.find((de) => de.serviceID === service.id);
                return total + (durationException?.duration ?? service.duration ?? 0);
            }
        }, 0);
    }

    isFirstAvailableEmployee(employee: Employee): boolean {
        return employee.id === '0' || employee.firstAvailable === true;
    }

    anyServiceHasDurationException(): boolean {
        return this.services?.some((service) => service.hasDurationException === true) ?? false;
    }

    shouldShowUpToForEmployee(employee: Employee): boolean {
        return this.isFirstAvailableEmployee(employee) && this.anyServiceHasDurationException();
    }

    getMinAndMaxEmployeePrice(employeeList: Employee[]): [number, number] {
        if (!this.services?.length) {
            return [0, 0];
        }

        // For First Available, we want to show the range from lowest to highest price
        // Use the lowestPrice from each service (sum of all service lowestPrices)
        const minPrice = this.services.reduce((sum, service) => sum + (service.lowestPrice ?? service.price ?? 0), 0);

        // For max price, calculate the highest total price across all employees
        const pricesPerEmployees = employeeList.map((employee) =>
            this.services!.reduce((total, service) => {
                const priceException = employee.employeeServicePriceList?.find((esp) => esp.serviceID === service.id);
                return total + (priceException?.price ?? service.price ?? 0);
            }, 0)
        );

        const maxPrice = pricesPerEmployees.length > 0 ? Math.max(...pricesPerEmployees) : minPrice;
        return [minPrice, maxPrice];
    }

    private previouslySelectedEmployee() {
        const appointmentRequest = this.localStorageService.get(APPOINTMENT_REQUEST) as AppointmentRequest;
        if (appointmentRequest?.employee?.length > 0) {
            if (appointmentRequest.employee[0].firstAvailable) {
                this.formGroup.patchValue({ employee: '0' });
            } else {
                this.formGroup.patchValue({ employee: appointmentRequest.employee[0].id });
            }
            this.navigationService.setIsStepFormValid(true);
        } else {
            this.navigationService.setIsStepFormValid(false);
        }
    }

    ngOnDestroy() {
        this.navigationButtonSubscription.unsubscribe();
        this.ngUnsubscribe.complete();
        this._destroyed$.next();
        this._destroyed$.complete();
    }
}
